import { CHANNEL_ID, MEMBER_ID, StringTMap, TENANT_ID } from '@@types/common';
import Member, { SharedChannelMember } from '@@types/Member';

import { MemberStatus } from '~/constants';
import { membersStore } from '~/stores/MembersStore';
import { uiState } from '~/stores/UiState';
import { isDormant, isLeaver } from '~/utils/memberUtil';
import { lift } from '~/utils/monadUtil';
import { isEmpty, isEqual } from 'lodash';
import { action, observable } from 'mobx';
import {
  __,
  always,
  assoc,
  curry,
  difference,
  dissoc,
  forEach,
  ifElse,
  isNil,
  map,
  mergeDeepRight,
  pipe,
  prop,
} from 'ramda';
import { Maybe } from 'ramda-fantasy';

class InterTenantMembersStore {
  @observable.shallow
  members: StringTMap<SharedChannelMember> = {};

  memberFetchedAt: StringTMap<number> = {};

  @observable.shallow
  membersStatus: StringTMap<MemberStatus> = {};

  memberChannelListMap: StringTMap<CHANNEL_ID[]> = {};

  channelMemberListMap: StringTMap<MEMBER_ID[]> = {};

  memberTenantMap: StringTMap<TENANT_ID> = {};

  memberStatusRequest = null;

  getById = (memberId: MEMBER_ID) => {
    return this.members[memberId] || membersStore.getById(memberId);
  };

  safeGetById = (memberId: MEMBER_ID): Maybe<Member> => {
    const member = this.getById(memberId);
    return isEmpty(member) ? Maybe.Nothing() : Maybe.Just(member);
  };

  getMembersById = (membersId: MEMBER_ID[]) => {
    return map(this.getById, membersId);
  };

  getStatusById = (id: MEMBER_ID) => {
    const member = this.getById(id) || {};
    if (isLeaver(member as SharedChannelMember) || isDormant(member)) {
      return MemberStatus.Offline;
    }

    return this.membersStatus[id] || membersStore.getStatusById(id) || MemberStatus.Offline;
  };

  @action
  setMembers = curry((channelId: CHANNEL_ID, memberList: SharedChannelMember[]) => {
    this.removeOldChannelInfo(channelId);
    const now = Date.now();
    memberList.forEach((member) => {
      this.members[member.id] = member;
      this.memberFetchedAt[member.id] = now;
      this.memberChannelListMap[member.id] = this.memberChannelListMap[member.id] || [];
      this.memberChannelListMap[member.id].push(channelId);
      this.memberTenantMap[member.id] = member.tenantId;
    });
  });

  @action
  removeOldChannelInfo = (channelId: CHANNEL_ID) => {
    const newMap = uiState.pickLruChannelIds(channelId)(this.channelMemberListMap);
    const [removedChannelId] = difference(Object.keys(this.channelMemberListMap), Object.keys(newMap));
    pipe<any, any, any>(
      lift(prop(__, this.channelMemberListMap)),
      map(
        forEach((memberId: MEMBER_ID) => {
          if (this.memberChannelListMap[memberId]?.length <= 1) {
            this.memberChannelListMap = dissoc(memberId, this.memberChannelListMap);
            this.memberTenantMap = dissoc(memberId, this.memberTenantMap);
            this.members = dissoc(memberId, this.members);
          }
        })
      )
    )(removedChannelId);
    this.channelMemberListMap = newMap;
  };

  @action
  setMember = (member: SharedChannelMember) => {
    this.members = assoc(member.id, member, this.members);
    return this.getById(member.id);
  };

  @action
  updateMember = (member: SharedChannelMember) => {
    this.members = assoc(member.id, mergeDeepRight(this.getById(member.id), member), this.members) as Record<
      string,
      SharedChannelMember
    >;
  };

  @action
  mergeMember = (member: SharedChannelMember) => {
    ifElse(always(isNil(this.getById(member.id))), this.setMember, this.updateMember)(member);
  };

  @action
  updateStatus = ({ id, status }) => {
    this.membersStatus[id] = status;
  };

  @action
  cacheMemberStatusRequest = (body) => {
    this.memberStatusRequest = body;
    return body;
  };

  @action
  onResponseMemberStatus = (body) => {
    if (isEqual(this.memberStatusRequest, body)) {
      this.memberStatusRequest = null;
    }
  };
}

export default InterTenantMembersStore;

export const interTenantMembersStore = new InterTenantMembersStore();
